import contentConfig from '../config/contentConfig.json';

export type ContentConfig = typeof contentConfig;

class ContentService {
  private static instance: ContentService;
  private config: ContentConfig;

  private constructor() {
    this.config = contentConfig;
  }

  public static getInstance(): ContentService {
    if (!ContentService.instance) {
      ContentService.instance = new ContentService();
    }
    return ContentService.instance;
  }

  // Get content by path (e.g., 'ui.buttons.send')
  public get(path: string): string {
    const keys = path.split('.');
    let current: unknown = this.config;
    
    for (const key of keys) {
      if (current && typeof current === 'object' && current !== null && key in current) {
        current = (current as Record<string, unknown>)[key];
      } else {
        // Content path not found, return the path as fallback
        return path;
      }
    }
    
    return typeof current === 'string' ? current : path;
  }

  // Get content with variable substitution
  public getWithVars(path: string, variables: Record<string, string | number>): string {
    let content = this.get(path);
    
    // Replace variables in format {variable}
    Object.entries(variables).forEach(([key, value]) => {
      content = content.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value));
    });
    
    return content;
  }

  // Get UI content
  public ui = {
    chatbot: {
      title: () => this.get('ui.chatbot.title'),
      minimizedTitle: () => this.get('ui.chatbot.minimizedTitle')
    },
    header: {
      logoText: () => this.get('ui.header.logoText'),
      welcomeTitle: () => this.get('ui.header.welcomeTitle')
    },
    buttons: {
      minimize: () => this.get('ui.buttons.minimize'),
      maximize: () => this.get('ui.buttons.maximize'),
      close: () => this.get('ui.buttons.close'),
      toggleSidebar: () => this.get('ui.buttons.toggleSidebar'),
      settings: () => this.get('ui.buttons.settings'),
      send: () => this.get('ui.buttons.send'),
      newChat: () => this.get('ui.buttons.newChat'),
      clear: () => this.get('ui.buttons.clear'),
      retry: () => this.get('ui.buttons.retry'),
      delete: () => this.get('ui.buttons.delete')
    },
    placeholders: {
      messageInput: () => this.get('ui.placeholders.messageInput'),
      searchSessions: () => this.get('ui.placeholders.searchSessions'),
      emptyState: () => this.get('ui.placeholders.emptyState')
    },
    labels: {
      you: () => this.get('ui.labels.you'),
      assistant: () => this.get('ui.labels.assistant'),
      typing: () => this.get('ui.labels.typing'),
      loading: () => this.get('ui.labels.loading'),
      sessions: () => this.get('ui.labels.sessions'),
      newSession: () => this.get('ui.labels.newSession')
    }
  };

  // Get messages
  public messages = {
    welcome: () => this.get('messages.welcome'),
    welcomeStandalone: () => this.get('messages.welcomeStandalone'),
    welcomeWidget: () => this.get('messages.welcomeWidget'),
    loading: () => this.get('messages.loading'),
    emptyState: {
      title: () => this.get('messages.emptyState.title'),
      description: () => this.get('messages.emptyState.description')
    }
  };

  // Get errors
  public errors = {
    chat: {
      sendFailed: () => this.get('errors.chat.sendFailed'),
      noActiveSession: () => this.get('errors.chat.noActiveSession'),
      messageEmpty: () => this.get('errors.chat.messageEmpty'),
      networkTimeout: () => this.get('errors.chat.networkTimeout'),
      serverError: () => this.get('errors.chat.serverError'),
      rateLimited: () => this.get('errors.chat.rateLimited')
    },
    session: {
      loadFailed: () => this.get('errors.session.loadFailed'),
      saveFailed: () => this.get('errors.session.saveFailed'),
      deleteFailed: () => this.get('errors.session.deleteFailed'),
      notFound: () => this.get('errors.session.notFound')
    },
    general: {
      unknownError: () => this.get('errors.general.unknownError'),
      tryAgain: () => this.get('errors.general.tryAgain'),
      contactSupport: () => this.get('errors.general.contactSupport')
    }
  };

  // Get confirmations
  public confirmations = {
    deleteSession: () => this.get('confirmations.deleteSession'),
    clearChat: () => this.get('confirmations.clearChat')
  };

  // Get tooltips
  public tooltips = {
    minimize: () => this.get('tooltips.minimize'),
    maximize: () => this.get('tooltips.maximize'),
    close: () => this.get('tooltips.close'),
    settings: () => this.get('tooltips.settings'),
    newChat: () => this.get('tooltips.newChat'),
    deleteSession: () => this.get('tooltips.deleteSession'),
    toggleSidebar: () => this.get('tooltips.toggleSidebar')
  };

  // Get metadata
  public metadata = {
    sessionTitles: {
      welcome: () => this.get('metadata.sessionTitles.welcome'),
      newChat: () => this.get('metadata.sessionTitles.newChat'),
      untitled: () => this.get('metadata.sessionTitles.untitled')
    }
  };

  // Update config (for dynamic content loading)
  public updateConfig(newConfig: Partial<ContentConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  // Get full config
  public getConfig(): ContentConfig {
    return this.config;
  }
}

// Export singleton instance
export const contentService = ContentService.getInstance();
export default contentService;
